"use client";

import { convertToAssetId, HydratedCanvas } from "@zapier/client";
import { CanvasDeleteModal } from "../CanvasDeleteModal/CanvasDeleteModal";
import { CanvasShareModal } from "../CanvasShareModal";
import { AssetDeleteModal, MoveAssetModal } from "@zapier/shared";
import { AssetTypes, FOLDER_TRASH_ID } from "@zapier/utils";
import { useMemo } from "react";
import { CanvasRenameModal } from "../CanvasRenameModal";
import CanvasChangeOwnerModal from "../CanvasChangeOwnerModal/CanvasChangeOwnerModal";

export enum CanvasModal {
  Delete = "canvas__delete",
  Share = "canvas__share",
  Move = "canvas__move",
  Rename = "canvas__rename",
  ChangeOwner = "canvas__changeOwner",
}

interface Props {
  currentModal: CanvasModal | undefined;
  currentCanvas: HydratedCanvas | undefined;
  setCurrentCanvas: (canvas?: HydratedCanvas | undefined) => void;
  setCurrentModal: (modal?: CanvasModal | undefined) => void;
  folderId?: string; // used for cache invalidation in mutations
}

export function CanvasModals({
  currentModal,
  currentCanvas,
  setCurrentCanvas,
  setCurrentModal,
  folderId,
}: Props) {
  const handleCloseModal = () => {
    setCurrentModal();
    setCurrentCanvas();
  };

  const assetIds = useMemo(() => {
    if (!currentCanvas?.id) {
      return;
    }
    return [convertToAssetId(AssetTypes.Canvas, currentCanvas.id)];
  }, [currentCanvas?.id]);

  const isTrashed = folderId === FOLDER_TRASH_ID;

  return (
    <>
      <CanvasChangeOwnerModal
        isOpen={currentModal === CanvasModal.ChangeOwner}
        onCloseModal={handleCloseModal}
        canvas={currentCanvas}
        folderId={folderId}
      />

      {isTrashed ? (
        <AssetDeleteModal
          selectedAssetIds={assetIds ?? []}
          isOpen={currentModal === CanvasModal.Delete}
          setIsOpen={handleCloseModal}
          folderId={folderId}
        />
      ) : (
        <CanvasDeleteModal
          canvas={currentCanvas}
          isOpen={currentModal === CanvasModal.Delete}
          onClose={handleCloseModal}
          folderId={folderId}
        />
      )}

      <CanvasRenameModal
        canvas={currentCanvas}
        isOpen={currentModal === CanvasModal.Rename}
        onClose={handleCloseModal}
        folderId={folderId}
      />

      <CanvasShareModal
        canvas={currentCanvas}
        isOpen={currentModal === CanvasModal.Share}
        onCloseModal={handleCloseModal}
      />

      <MoveAssetModal
        assetType={AssetTypes.Canvas}
        assetIds={assetIds}
        assetTitle={currentCanvas?.name}
        isOpen={currentModal === CanvasModal.Move}
        setIsOpen={handleCloseModal}
        assetParentId={folderId}
      />
    </>
  );
}
